import { useEffect, useMemo, useState } from "react";
import { Mail, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { PublicHeader } from "../components/PublicHeader";
import { NewsCard } from "../components/NewsCard";
import { AdSlot } from "../components/AdSlot";

export default function HomePage() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [frontAd, setFrontAd] = useState(null);

  const loadData = async (category = activeCategory) => {
    setLoading(true);
    try {
      const [catRes, postRes] = await Promise.all([
        api.get("/categories"),
        api.get("/posts", { params: { category } }),
      ]);
      setCategories(catRes.data);
      setPosts(postRes.data);
    } catch (error) {
      toast.error("செய்திகளை ஏற்ற முடியவில்லை");
    } finally {
      setLoading(false);
    }
  };

  const loadFrontAd = async () => {
    try {
      const response = await api.get("/ads", { params: { placement: "frontpage" } });
      setFrontAd(response.data[0] || null);
    } catch (error) {
      setFrontAd(null);
    }
  };

  useEffect(() => { loadData("all"); loadFrontAd(); }, []);

  const featured = posts.find((post) => post.featured) || posts[0];
  const rest = posts.filter((post) => post.id !== featured?.id);
  const trending = useMemo(() => posts.filter((post) => post.trending).slice(0, 4), [posts]);

  const changeCategory = (slug) => {
    setActiveCategory(slug);
    loadData(slug);
  };

  const subscribe = async (event) => {
    event.preventDefault();
    try {
      await api.post("/subscribe", { email });
      setEmail("");
      toast.success("சந்தா பதிவு செய்யப்பட்டது");
    } catch (error) {
      toast.error("சரியான மின்னஞ்சலை உள்ளிடவும்");
    }
  };

  return (
    <div data-testid="home-page">
      <PublicHeader />
      <main>
        <section className="border-b border-slate-200 bg-slate-50 py-5" data-testid="frontpage-large-ad-section">
          <div className="vtc-container" data-testid="frontpage-large-ad-container">
            <AdSlot size="frontpage" label="Main Front Page Advertisement" ad={frontAd} />
          </div>
        </section>
        <section className="border-b border-slate-200 bg-white" data-testid="home-top-strip">
          <div className="vtc-container grid gap-5 py-5 lg:grid-cols-[1fr_340px]">
            <div className="flex flex-wrap items-center gap-3 overflow-x-auto" data-testid="category-tabs">
              <button onClick={() => changeCategory("all")} className={`px-4 py-2 text-sm font-black transition-colors duration-200 ${activeCategory === "all" ? "bg-blue-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:border-blue-900"}`} data-testid="category-tab-all">அனைத்தும்</button>
              {categories.map((category) => (
                <button key={category.id} onClick={() => changeCategory(category.slug)} className={`px-4 py-2 text-sm font-black transition-colors duration-200 ${activeCategory === category.slug ? "bg-blue-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:border-blue-900"}`} data-testid={`category-tab-${category.slug}`}>{category.tamil_name}</button>
              ))}
            </div>
            <div className="flex items-center gap-3 border border-slate-200 bg-slate-50 px-4 py-2" data-testid="search-display-box">
              <Search className="h-4 w-4 text-green-800" aria-hidden="true" />
              <span className="text-sm font-bold text-slate-600" data-testid="search-display-text">தமிழ் செய்திகள் • சமூக அறிவிப்புகள்</span>
            </div>
          </div>
        </section>

        <section className="vtc-container py-8 sm:py-10" data-testid="homepage-news-section">
          <div className="mb-7 flex flex-col gap-2" data-testid="homepage-heading-wrap">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-green-800" data-testid="homepage-kicker">Latest News</p>
            <h1 className="text-4xl font-black leading-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="homepage-title">வன்னியன் தமிழ் சமூக செய்திகள்</h1>
          </div>
          <AdSlot size="wide" label="Top Banner Advertisement" />
          {loading ? (
            <div className="mt-8 border border-slate-200 bg-white p-10 text-center font-bold text-slate-600" data-testid="home-loading-state">செய்திகள் ஏற்றப்படுகின்றன...</div>
          ) : posts.length === 0 ? (
            <div className="mt-8 border border-slate-200 bg-white p-10 text-center font-bold text-slate-600" data-testid="home-empty-state">செய்திகள் இல்லை</div>
          ) : (
            <div className="mt-8 grid auto-rows-fr gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="news-grid">
              {featured && <NewsCard post={featured} large index={0} />}
              {rest.map((post, index) => <NewsCard key={post.id} post={post} index={index + 1} />)}
            </div>
          )}
        </section>

        <section id="trending" className="border-y border-slate-200 bg-white py-10" data-testid="trending-section">
          <div className="vtc-container grid gap-8 lg:grid-cols-[1fr_320px]">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.22em] text-green-800" data-testid="trending-kicker">Trending</p>
              <h2 className="mt-2 text-3xl font-black text-slate-950" data-testid="trending-title">பிரபலமான செய்திகள்</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="trending-list">
                {trending.length > 0 ? trending.map((post) => <NewsCard key={post.id} post={post} />) : (
                  <div className="border border-slate-200 bg-slate-50 p-6 font-bold text-slate-600 sm:col-span-2" data-testid="trending-empty-state">
                    இந்த பிரிவில் தற்போது பிரபலமான செய்திகள் இல்லை.
                  </div>
                )}
              </div>
            </div>
            <AdSlot size="sidebar" label="Sidebar Advertisement" />
          </div>
        </section>

        <section id="newsletter" className="vtc-container py-10" data-testid="newsletter-section">
          <div className="grid gap-8 border border-slate-200 bg-blue-950 p-6 text-white sm:p-8 lg:grid-cols-[1fr_420px]" data-testid="newsletter-panel">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.22em] text-green-300" data-testid="newsletter-kicker">Newsletter</p>
              <h2 className="mt-2 text-3xl font-black" data-testid="newsletter-title">செய்திகளை நேரடியாக பெறுங்கள்</h2>
              <p className="mt-3 text-base leading-relaxed text-blue-100" data-testid="newsletter-description">புதிய கட்டுரைகள், அறிவிப்புகள் மற்றும் சமூக நிகழ்வுகளுக்கான புதுப்பிப்புகளை பெற மின்னஞ்சலை பதிவு செய்யுங்கள்.</p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row lg:flex-col" data-testid="newsletter-form">
              <label className="sr-only" htmlFor="subscriber-email">Email</label>
              <input id="subscriber-email" value={email} onChange={(event) => setEmail(event.target.value)} type="email" required placeholder="your@email.com" className="min-h-12 flex-1 border border-white/20 bg-white px-4 text-slate-950 focus:outline-none focus:ring-2 focus:ring-green-300" data-testid="newsletter-email-input" />
              <button type="submit" className="inline-flex min-h-12 items-center justify-center gap-2 bg-green-700 px-6 font-black text-white transition-colors duration-200 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2 focus:ring-offset-blue-950" data-testid="newsletter-submit-button"><Mail className="h-5 w-5" aria-hidden="true" />சந்தா</button>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}